/**
 * Product card image carousel: dots, desktop drag, and image tap navigation.
 */
if (!customElements.get('ax-card-media-slider')) {
  customElements.define(
    'ax-card-media-slider',
    class AxCardMediaSlider extends HTMLElement {
      connectedCallback() {
        this.slides = this.querySelector('.ax-card__slides');
        this.dots = Array.from(this.querySelectorAll('.ax-card__dot'));
        if (!this.slides || this.dots.length < 2) return;

        this.index = 0;
        this._dragFrame = null;
        this._suppressClickUntil = 0;
        this._targetScrollLeft = 0;
        this._abort = new AbortController();
        const signal = this._abort.signal;

        const wrapper = this.closest('.card-wrapper');
        this.link = wrapper ? wrapper.querySelector('.card__heading a, a.full-unstyled-link') : null;

        this.dots.forEach((dot, i) => {
          dot.addEventListener(
            'click',
            (event) => {
              event.preventDefault();
              event.stopPropagation();
              this.goTo(i);
            },
            { signal }
          );
        });

        this._ticking = false;
        this.slides.addEventListener(
          'scroll',
          () => {
            if (this._ticking) return;
            this._ticking = true;
            requestAnimationFrame(() => {
              this.syncActive();
              this._ticking = false;
            });
          },
          { passive: true, signal }
        );

        this.initPointerDrag(signal);
      }

      disconnectedCallback() {
        if (this._abort) {
          this._abort.abort();
          this._abort = null;
        }
        if (this._dragFrame) {
          cancelAnimationFrame(this._dragFrame);
          this._dragFrame = null;
        }
      }

      get slideWidth() {
        return this.slides.clientWidth || 1;
      }

      get maxScrollLeft() {
        return Math.max(0, this.slides.scrollWidth - this.slides.clientWidth);
      }

      goTo(index) {
        const max = this.dots.length - 1;
        const i = Math.max(0, Math.min(index, max));
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this._targetScrollLeft = Math.min(i * this.slideWidth, this.maxScrollLeft);
        this.setActive(i);
        this.slides.scrollTo({ left: this._targetScrollLeft, behavior: reduce ? 'auto' : 'smooth' });
      }

      syncActive() {
        const i = Math.max(0, Math.min(Math.round(this.slides.scrollLeft / this.slideWidth), this.dots.length - 1));
        this.setActive(i);
      }

      setActive(index) {
        const i = Math.max(0, Math.min(index, this.dots.length - 1));
        if (i === this.index) return;

        this.index = i;
        this.dots.forEach((dot, j) => {
          const isActive = j === i;
          dot.classList.toggle('is-active', isActive);
          dot.setAttribute('aria-current', isActive ? 'true' : 'false');
        });
      }

      queueDragFrame() {
        if (this._dragFrame) return;

        this._dragFrame = requestAnimationFrame(() => {
          this.slides.scrollLeft = this._targetScrollLeft;
          this.syncActive();
          this._dragFrame = null;
        });
      }

      getDragTargetIndex(startIndex, dragDistance, velocity) {
        const velocityThreshold = 0.45;
        const distanceThreshold = Math.min(72, this.slideWidth * 0.16);
        let targetIndex = startIndex;

        if (Math.abs(velocity) > velocityThreshold && Math.abs(dragDistance) > 12) {
          targetIndex = startIndex + (velocity < 0 ? 1 : -1);
        } else if (Math.abs(dragDistance) > distanceThreshold) {
          targetIndex = startIndex + (dragDistance < 0 ? 1 : -1);
        }

        return Math.max(0, Math.min(targetIndex, this.dots.length - 1));
      }

      initPointerDrag(signal) {
        let down = false;
        let moved = false;
        let startX = 0;
        let startScroll = 0;
        let startIndex = 0;
        let lastX = 0;
        let lastTime = 0;
        let velocity = 0;

        this.slides.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'touch' || event.button !== 0) return;

          down = true;
          moved = false;
          startX = event.clientX;
          startScroll = this.slides.scrollLeft;
          startIndex = Math.round(startScroll / this.slideWidth);
          lastX = event.clientX;
          lastTime = performance.now();
          velocity = 0;

          this._targetScrollLeft = startScroll;
          this.slides.classList.add('is-grabbing');
          this.slides.style.scrollBehavior = 'auto';
          this.slides.setPointerCapture(event.pointerId);
          event.preventDefault();
        }, { signal });

        this.slides.addEventListener('pointermove', (event) => {
          if (!down) return;

          const dx = event.clientX - startX;
          const now = performance.now();
          const dt = Math.max(16, now - lastTime);
          const instantVelocity = (event.clientX - lastX) / dt;
          velocity = velocity * 0.75 + instantVelocity * 0.25;
          lastX = event.clientX;
          lastTime = now;

          if (Math.abs(dx) > 3) moved = true;

          if (moved) {
            this._targetScrollLeft = Math.max(0, Math.min(startScroll - dx, this.maxScrollLeft));
            this.queueDragFrame();
            event.preventDefault();
          }
        }, { signal });

        const endDrag = (event) => {
          if (!down) return;

          down = false;
          this.slides.classList.remove('is-grabbing');
          this.slides.style.scrollBehavior = '';

          if (this._dragFrame) {
            cancelAnimationFrame(this._dragFrame);
            this._dragFrame = null;
            this.slides.scrollLeft = this._targetScrollLeft;
          }

          if (this.slides.hasPointerCapture(event.pointerId)) {
            this.slides.releasePointerCapture(event.pointerId);
          }

          if (moved) {
            const endX = event.clientX;
            this._suppressClickUntil = performance.now() + 350;
            this.goTo(this.getDragTargetIndex(startIndex, endX - startX, velocity));
          }
        };

        this.slides.addEventListener('pointerup', endDrag, { signal });
        this.slides.addEventListener('pointercancel', endDrag, { signal });

        this.slides.addEventListener('click', (event) => {
          if (performance.now() < this._suppressClickUntil) {
            event.preventDefault();
            event.stopPropagation();
            return;
          }

          if (event.defaultPrevented) return;
          if (!this.link || !this.link.href) return;

          if (event.metaKey || event.ctrlKey || event.shiftKey) {
            window.open(this.link.href, '_blank');
          } else {
            window.location.href = this.link.href;
          }
        }, { signal });
      }
    }
  );
}
